<template>
  <div class="order-detail-container">
    <!-- Professional Header -->
    <div class="page-header">
      <div class="header-top">
        <button @click="goBack" class="back-button">
          <i class="fas fa-arrow-left"></i>
          <span>Zurück</span>
        </button>
        <div class="header-actions">
          <span v-if="isEditing && hasUnsavedChanges" class="unsaved-pill">
            <i class="fas fa-circle"></i> Ungespeichert
          </span>
          <template v-if="!isEditing">
            <button v-if="canEdit" class="btn-action btn-edit" @click="startEdit">
              <i class="fas fa-edit"></i>
              <span>Bearbeiten</span>
            </button>
            <span v-else class="readonly-hint">Nur Lesezugriff</span>
          </template>
          <template v-else>
            <button class="btn-action btn-cancel" @click="cancelEdit" :disabled="saving">
              <i class="fas fa-times"></i>
              <span>Abbrechen</span>
            </button>
            <button class="btn-action btn-save" @click="saveOrder" :disabled="saving">
              <i v-if="saving" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-save"></i>
              <span>Speichern</span>
            </button>
          </template>
        </div>
      </div>

      <div class="header-main">
        <div class="order-info">
          <div class="order-icon">
            <i class="fas fa-file-invoice"></i>
          </div>
          <div>
            <h1 class="order-title">
              Auftrag {{ order.order_number }}
              <span v-if="order.invoice_number" class="invoice-suffix">Rechnung: {{ order.invoice_number }}</span>
            </h1>
            <div class="order-meta">
              <span v-if="customer" class="meta-item">
                <i class="fas fa-building"></i>
                <router-link :to="`/kunden/${customer.id}`" class="customer-link">
                  {{ customer.name }}
                </router-link>
              </span>
              <span v-if="employee" class="meta-item">
                <i class="fas fa-user"></i>
                <router-link :to="`/mitarbeiter/${employee.id}`" class="customer-link">
                  {{ employee.name }}
                </router-link>
              </span>
              <span v-else-if="order.legacy_employee_name" class="meta-item">
                <i class="fas fa-user"></i>
                {{ order.legacy_employee_name }}
              </span>
              <span v-if="order.created_at" class="meta-item">
                <i class="fas fa-calendar"></i>
                {{ formatDate(order.created_at) }}
              </span>
            </div>
          </div>
        </div>
        <div class="status-badges">
          <span :class="['status-badge', getStatusClass(editableOrder.status)]">
            <i class="fas fa-circle"></i>
            {{ getStatusText(editableOrder.status) }}
          </span>
          <span :class="['priority-badge', getPriorityBadgeClass(editableOrder.priority)]">
            <i class="fas fa-flag"></i>
            {{ getPriorityText(editableOrder.priority) }}
          </span>
        </div>
      </div>

      <!-- Validation errors banner -->
      <div v-if="saveErrors.length" class="errors-banner">
        <i class="fas fa-exclamation-triangle"></i>
        <div>
          <strong>Speichern fehlgeschlagen:</strong>
          <span v-for="(err, i) in saveErrors" :key="i">
            {{ err }}<span v-if="i < saveErrors.length - 1">; </span>
          </span>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-container">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Lädt…</span>
      </div>
    </div>

    <!-- Main Content -->
    <form v-else @submit.prevent="saveOrder" class="content-grid">

      <!-- ─── LEFT COLUMN ─── -->
      <div class="details-section">

        <!-- Grunddaten -->
        <div class="info-card">
          <div class="info-card-header">
            <i class="fas fa-info-circle"></i>
            <h3>Grunddaten</h3>
          </div>
          <div class="info-card-body">
            <div class="info-row">
              <span class="info-label">Auftragsnr.</span>
              <input
                v-model="editableOrder.order_number"
                type="text"
                maxlength="8"
                class="edit-input"
                :disabled="!isEditing || !isOwner"
              >
            </div>
            <div class="info-row">
              <span class="info-label">Rechnungsnr.</span>
              <input
                v-model="editableOrder.invoice_number"
                type="text"
                maxlength="8"
                class="edit-input"
                :disabled="!isEditing"
              >
            </div>
            <div class="info-row">
              <span class="info-label">Beschreibung</span>
              <div>
                <input
                  v-model="editableOrder.description"
                  type="text"
                  maxlength="70"
                  class="edit-input"
                  :class="{ 'has-error': fieldError('description') }"
                  :disabled="!isEditing"
                >
                <div v-if="fieldError('description')" class="field-error">{{ fieldError('description') }}</div>
              </div>
            </div>
            <div class="info-row">
              <span class="info-label">Mitarbeiter</span>
              <filter-dropdown
                :model-value="editableOrder.employee_id != null ? String(editableOrder.employee_id) : ''"
                :options="employeeOptions()"
                label="Mitarbeiter"
                placeholder="Nicht zugewiesen"
                :show-avatars="true"
                :block="true"
                :disabled="!isEditing"
                @update:modelValue="val => editableOrder.employee_id = val ? parseInt(val) : null"
              />
            </div>
            <div class="info-row">
              <span class="info-label">Standort</span>
              <filter-dropdown
                v-model="editableOrder.pk_sb"
                :options="standortOptions()"
                label="Standort"
                placeholder="Nicht gesetzt"
                :block="true"
                :disabled="!isEditing"
              />
            </div>
          </div>
        </div>

        <!-- Status & Zahlung -->
        <div class="info-card">
          <div class="info-card-header">
            <i class="fas fa-tasks"></i>
            <h3>Status & Zahlung</h3>
          </div>
          <div class="info-card-body">
            <div class="info-row">
              <span class="info-label">Status</span>
              <filter-dropdown
                v-model="editableOrder.status"
                :options="statusOptions()"
                label="Status"
                placeholder="Kein Status"
                :show-colors="true"
                :block="true"
                :disabled="!isEditing"
                :clearable="false"
              />
            </div>
            <!-- #17 — Erstplanung: importierte Aufträge stehen grau auf dem
                 Brett, bis der Sachbearbeiter sie bestätigt. -->
            <div class="info-row">
              <span class="info-label">Planung</span>
              <div>
                <filter-dropdown
                  v-model="editableOrder.planning_state"
                  :options="planningStateOptions"
                  label="Planung"
                  placeholder="Bestätigt"
                  :block="true"
                  :disabled="!isEditing || !canPlan"
                  :clearable="false"
                />
                <div class="field-hint">
                  Entwurf = grau in der Kapazitätsplanung (Termine noch ungeprüft)
                </div>
              </div>
            </div>
            <div class="info-row">
              <span class="info-label">Zahlung</span>
              <filter-dropdown
                v-model="editableOrder.payment_status"
                :options="paymentOptions()"
                label="Zahlung"
                placeholder="Nicht gesetzt"
                :show-colors="true"
                :block="true"
                :disabled="!isEditing"
                :clearable="false"
              />
            </div>
            <div class="info-row">
              <span class="info-label">Priorität</span>
              <filter-dropdown
                v-model="editableOrder.priority"
                :options="priorityOptions()"
                label="Priorität"
                placeholder="Nicht gesetzt"
                :show-colors="true"
                :block="true"
                :disabled="!isEditing"
                :clearable="false"
              />
            </div>
            <div class="info-row">
              <span class="info-label">Versandart</span>
              <filter-dropdown
                :model-value="String(editableOrder.delivery_method ?? '')"
                :options="deliveryOptions()"
                label="Versandart"
                placeholder="Nicht gesetzt"
                :block="true"
                :disabled="!isEditing"
                :clearable="false"
                @update:modelValue="val => editableOrder.delivery_method = val !== '' ? parseInt(val) : 0"
              />
            </div>
          </div>
        </div>

        <!-- Produktion -->
        <div class="info-card">
          <div class="info-card-header">
            <i class="fas fa-print"></i>
            <h3>Produktion</h3>
          </div>
          <div class="info-card-body">
            <div class="info-row">
              <span class="info-label">Auflage</span>
              <div>
                <input
                  v-model="editableOrder.print_run"
                  type="text"
                  inputmode="numeric"
                  placeholder="z.B. 1000"
                  class="edit-input"
                  :disabled="!isEditing"
                >
                <div v-if="isEditing" class="field-hint">
                  Zahl oder Freitext – z.B. „50.000", „ca. 1000", „50x"
                </div>
              </div>
            </div>
            <div class="info-row">
              <span class="info-label">Seitenzahl</span>
              <div>
                <input
                  v-model="editableOrder.page_count"
                  type="text"
                  inputmode="numeric"
                  class="edit-input"
                  :disabled="!isEditing"
                >
                <div v-if="isEditing" class="field-hint">
                  Zahl oder Freitext erlaubt
                </div>
              </div>
            </div>
            <div class="info-row">
              <span class="info-label">Druckformen / Durchgänge</span>
              <div>
                <input
                  v-model="editableOrder.druckform_count"
                  type="number"
                  min="0"
                  inputmode="numeric"
                  placeholder="z.B. 2"
                  class="edit-input"
                  :disabled="!isEditing"
                >
                <div v-if="isEditing" class="field-hint">
                  Anzahl Druckplatten-Sätze / Durchgänge (Basis für die Stundenkalkulation)
                </div>
              </div>
            </div>
            <div class="info-row">
              <span class="info-label">Farbe</span>
              <input
                v-model="editableOrder.color"
                type="text"
                placeholder="4/4"
                class="edit-input"
                :disabled="!isEditing"
              >
            </div>
            <div class="info-row">
              <span class="info-label">Format</span>
              <input
                v-model="editableOrder.format"
                type="text"
                placeholder="A4, DIN lang …"
                class="edit-input"
                :disabled="!isEditing"
              >
            </div>
            <!-- A-5 — feste Auswahl 60/70/80; abweichende Altwerte bleiben wählbar -->
            <div class="info-row">
              <span class="info-label">Rastergröße</span>
              <div>
                <filter-dropdown
                  v-model="editableOrder.raster_output"
                  :options="rasterOptions"
                  label="Rastergröße"
                  placeholder="Nicht gesetzt"
                  :block="true"
                  :disabled="!isEditing"
                />
                <div class="field-hint">Standard: 70er</div>
              </div>
            </div>
            <div class="info-row">
              <span class="info-label">Farbigkeit</span>
              <input
                v-model="editableOrder.farbigkeit"
                type="text"
                placeholder="z.B. CMYK"
                class="edit-input"
                :disabled="!isEditing"
              >
            </div>
            <!-- #8 — die Maschine wird in „Maschinen-Belegung" gepflegt; hier nur
                 die Hauptmaschine als Anzeige, damit es keine zwei Wahrheiten gibt. -->
            <div class="info-row">
              <span class="info-label">Hauptmaschine</span>
              <div>
                <span class="info-value">{{ primaryMachine || 'Nicht zugewiesen' }}</span>
                <!-- #11 — make the consequence of picking Extern explicit -->
                <div v-if="isExtern" class="extern-hint">
                  <span class="extern-hint-dot"></span>
                  Komplett extern — Auftrag wird ausgelagert und mit rotem Ring markiert.
                </div>
                <div class="field-hint">Ändern unter „Maschinen-Belegung"</div>
              </div>
            </div>
            <div class="info-row">
              <span class="info-label">Druckart</span>
              <div class="checkbox-row">
                <label class="check-wrap">
                  <input v-model="editableOrder.is_schoen_wider" type="checkbox" :disabled="!isEditing">
                  <span>S+W</span>
                </label>
                <label class="check-wrap">
                  <input v-model="editableOrder.is_umschlagen" type="checkbox" :disabled="!isEditing">
                  <span>US</span>
                </label>
                <label class="check-wrap">
                  <input v-model="editableOrder.is_umstuelpen" type="checkbox" :disabled="!isEditing">
                  <span>UP</span>
                </label>
                <label class="check-wrap" title="mit Wendeeinrichtung">
                  <input v-model="editableOrder.is_wendeeinrichtung" type="checkbox" :disabled="!isEditing">
                  <span>WE</span>
                </label>
              </div>
            </div>
            <div class="info-row">
              <span class="info-label">Position</span>
              <div>
                <input
                  v-model="editableOrder.position_code"
                  type="text"
                  placeholder="z.B. H3x"
                  class="edit-input"
                  :disabled="!isEditing"
                >
                <div class="field-hint">Pauschal-Code (B2x, H3x, X1 …) — Preise aus der Positionen-Tabelle</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Maschinen-Belegung (#8) — ein Auftrag kann auf mehreren Maschinen laufen -->
        <div class="info-card">
          <div class="info-card-header">
            <i class="fas fa-industry"></i>
            <h3>Maschinen-Belegung</h3>
            <span class="ma-count">{{ assignments.length }}</span>
          </div>
          <div class="info-card-body">
            <p v-if="!assignments.length" class="ma-empty">
              Keine Maschine zugewiesen — der Auftrag erscheint nicht in der Kapazitätsplanung.
            </p>

            <div v-for="(a, i) in assignments" :key="a._key" class="ma-row">
              <span class="ma-pos" :title="i === 0 ? 'Hauptmaschine' : `Maschine ${i + 1}`">{{ i + 1 }}</span>

              <div class="ma-fields">
                <div class="ma-field ma-machine">
                  <label>Maschine</label>
                  <filter-dropdown
                    v-model="a.maschine_name"
                    :options="machineOptions()"
                    label="Maschine"
                    placeholder="Nicht zugewiesen"
                    :block="true"
                    :disabled="!isEditing"
                  />
                </div>
                <div class="ma-field">
                  <label>Geplant von</label>
                  <input v-model="a.geplant_start" type="datetime-local" class="edit-input" :disabled="!isEditing">
                </div>
                <div class="ma-field">
                  <label>Geplant bis</label>
                  <input v-model="a.geplant_ende" type="datetime-local" class="edit-input" :disabled="!isEditing">
                </div>
                <div class="ma-field ma-narrow">
                  <label>Menge</label>
                  <input v-model="a.geplante_menge" type="number" min="0" class="edit-input" :disabled="!isEditing">
                </div>
                <div class="ma-field ma-narrow">
                  <label>Schicht</label>
                  <select v-model="a.schicht_nr" class="edit-input" :disabled="!isEditing">
                    <option :value="null">—</option>
                    <option :value="1">1 (06–14)</option>
                    <option :value="2">2 (14–24)</option>
                  </select>
                </div>
              </div>

              <div class="ma-actions">
                <button
                  type="button"
                  class="ma-btn"
                  :disabled="!isEditing || i === 0"
                  title="Nach oben"
                  @click="moveAssignment(i, -1)"
                >
                  <i class="fas fa-arrow-up"></i>
                </button>
                <button
                  type="button"
                  class="ma-btn"
                  :disabled="!isEditing || i === assignments.length - 1"
                  title="Nach unten"
                  @click="moveAssignment(i, 1)"
                >
                  <i class="fas fa-arrow-down"></i>
                </button>
                <button
                  type="button"
                  class="ma-btn ma-btn-danger"
                  :disabled="!isEditing"
                  title="Maschine entfernen"
                  @click="removeAssignment(i)"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </div>

            <button v-if="isEditing" type="button" class="ma-add" @click="addAssignment">
              <i class="fas fa-plus"></i> Maschine hinzufügen
            </button>
            <div class="field-hint">
              Die erste Maschine ist die Hauptmaschine — sie füllt die Maschinen-Spalte in
              Listen und Ausdrucken. Jede Zeile belegt ihre eigene Kap.Pl.-Lane.
            </div>
          </div>
        </div>

        <!-- Abmessungen -->
        <div class="info-card">
          <div class="info-card-header">
            <i class="fas fa-ruler-combined"></i>
            <h3>Abmessungen</h3>
          </div>
          <div class="info-card-body">
            <div class="info-row">
              <span class="info-label">Endformat</span>
              <div class="dimension-group">
                <input
                  v-model="editableOrder.final_width"
                  type="number"
                  step="0.1"
                  placeholder="Breite"
                  class="edit-input"
                  :disabled="!isEditing"
                >
                <span class="dim-x">×</span>
                <input
                  v-model="editableOrder.final_length"
                  type="number"
                  step="0.1"
                  placeholder="Länge"
                  class="edit-input"
                  :disabled="!isEditing"
                >
                <span class="dim-unit">cm</span>
              </div>
            </div>
            <div class="info-row">
              <span class="info-label">Druckformat</span>
              <div class="dimension-group">
                <input
                  v-model="editableOrder.print_width"
                  type="number"
                  step="0.1"
                  placeholder="Breite"
                  class="edit-input"
                  :disabled="!isEditing"
                >
                <span class="dim-x">×</span>
                <input
                  v-model="editableOrder.print_length"
                  type="number"
                  step="0.1"
                  placeholder="Länge"
                  class="edit-input"
                  :disabled="!isEditing"
                >
                <span class="dim-unit">cm</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Material & Papier -->
        <div class="info-card">
          <div class="info-card-header">
            <i class="fas fa-scroll"></i>
            <h3>Material & Papier</h3>
          </div>
          <div class="info-card-body">
            <div class="info-row">
              <span class="info-label">Papiertyp</span>
              <input
                v-model="editableOrder.paper_type"
                type="text"
                class="edit-input"
                :disabled="!isEditing"
              >
            </div>
            <div class="info-row">
              <span class="info-label">Grammatur</span>
              <div class="dimension-group">
                <input
                  v-model="editableOrder.paper_weight"
                  type="number"
                  placeholder="120"
                  class="edit-input"
                  :disabled="!isEditing"
                >
                <span class="dim-unit">g/m²</span>
              </div>
            </div>
            <div class="info-row">
              <span class="info-label">Materialstatus</span>
              <filter-dropdown
                v-model="editableOrder.material_status"
                :options="materialStatusOptions()"
                label="Materialstatus"
                placeholder="–"
                :block="true"
                :disabled="!isEditing"
              />
            </div>
            <div class="info-row">
              <span class="info-label">Papier bestellt</span>
              <div class="checkbox-row">
                <label class="check-wrap">
                  <input
                    v-model="editableOrder.papier_bestellt"
                    type="checkbox"
                    :disabled="!isEditing"
                  >
                  <span>Bestellt</span>
                </label>
                <input
                  v-model="editableOrder.papier_bestellt_am"
                  type="date"
                  class="edit-input edit-input-inline"
                  :disabled="!isEditing || !editableOrder.papier_bestellt"
                >
              </div>
            </div>
            <div class="info-row">
              <span class="info-label">Papier angeliefert</span>
              <label class="check-wrap">
                <input
                  v-model="editableOrder.papier_angeliefert"
                  type="checkbox"
                  :disabled="!isEditing"
                >
                <span>Angeliefert</span>
              </label>
            </div>
            <div class="info-row">
              <span class="info-label">Druckbereit</span>
              <label class="check-wrap">
                <input
                  v-model="editableOrder.druckbereit_anzeige"
                  type="checkbox"
                  :disabled="!isEditing"
                >
                <span>Sichtbar in Druckplanung</span>
              </label>
            </div>
          </div>
        </div>

        <!-- Termine -->
        <div class="info-card">
          <div class="info-card-header">
            <i class="fas fa-calendar-alt"></i>
            <h3>Termine</h3>
          </div>
          <div class="info-card-body">
            <div class="info-row">
              <span class="info-label">Liefertermin</span>
              <input
                v-model="deliveryDate"
                type="datetime-local"
                class="edit-input"
                :disabled="!isEditing"
              >
            </div>
            <div class="info-row">
              <span class="info-label">Drucktermin</span>
              <input
                v-model="printDate"
                type="datetime-local"
                class="edit-input"
                :disabled="!isEditing"
              >
            </div>
            <div v-if="order.prepress_at" class="info-row">
              <span class="info-label">Vorstufe</span>
              <span class="info-value">{{ formatDateTime(order.prepress_at) }}</span>
            </div>
            <div v-if="order.plates_at" class="info-row">
              <span class="info-label">Platten</span>
              <span class="info-value">{{ formatDateTime(order.plates_at) }}</span>
            </div>
            <div v-if="order.printing_at" class="info-row">
              <span class="info-label">Druck</span>
              <span class="info-value">{{ formatDateTime(order.printing_at) }}</span>
            </div>
            <div v-if="order.processing_at" class="info-row">
              <span class="info-label">Weiterverarbeitung</span>
              <span class="info-value">{{ formatDateTime(order.processing_at) }}</span>
            </div>
            <div v-if="order.shipping_at" class="info-row">
              <span class="info-label">Versand</span>
              <span class="info-value">{{ formatDateTime(order.shipping_at) }}</span>
            </div>
            <div v-if="order.delivered_at" class="info-row">
              <span class="info-label">Ausgeliefert</span>
              <span class="info-value">{{ formatDateTime(order.delivered_at) }}</span>
            </div>
          </div>
        </div>

        <!-- Etappen-Daten (A-2) -->
        <div class="info-card">
          <div class="info-card-header">
            <i class="fas fa-clipboard-check"></i>
            <h3>Etappe</h3>
          </div>
          <div class="info-card-body">
            <div class="info-row">
              <span class="info-label">Verbrauchtes Papier (Bogen)</span>
              <input
                v-model.number="editableOrder.verbrauchtes_papier"
                type="number"
                min="0"
                class="edit-input"
                placeholder="Pflichtfeld vor Fertig (Druck)"
                :disabled="!isEditing"
              />
            </div>
            <div class="info-row">
              <span class="info-label">Partner-/Fremdauftrag</span>
              <label class="chk-inline">
                <input type="checkbox" v-model="editableOrder.partner_order" :disabled="!isEditing" />
                <span>Grauer Punkt (z. B. Agentur im Hause, „2K")</span>
              </label>
            </div>
            <div class="info-row">
              <span class="info-label">Plattenanzahl (CtP)</span>
              <input
                v-model.number="editableOrder.plattenanzahl"
                type="number"
                min="0"
                class="edit-input"
                placeholder="Anzahl gefertigter Druckplatten"
                :disabled="!isEditing"
              />
            </div>
            <div class="info-row">
              <span class="info-label">Druckfreigabe — Stunden</span>
              <input v-model.number="editableOrder.druckfreigabe_stunden" type="number" step="0.25" min="0" class="edit-input" :disabled="!isEditing" />
            </div>
            <div class="info-row">
              <span class="info-label">Druckfreigabe — Datum</span>
              <input v-model="editableOrder.druckfreigabe_datum" type="date" class="edit-input" :disabled="!isEditing" />
            </div>
            <div class="info-row stage-crew-row">
              <span class="info-label">
                Weitere Bearbeiter ({{ getStatusText(editableOrder.status) }})
                <small>max. {{ MAX_CREW }} je Etappe</small>
              </span>
              <div class="stage-crew">
                <div v-for="row in crewForStage" :key="row._key" class="stage-crew-line">
                  <select v-model.number="row.user_id" class="edit-input" :disabled="!isEditing">
                    <option :value="null">— Mitarbeiter wählen —</option>
                    <option v-for="e in employeeList" :key="e.id" :value="e.id">
                      {{ e.vorname }} {{ e.nachname }}
                    </option>
                  </select>
                  <button
                    v-if="isEditing"
                    type="button"
                    class="crew-remove"
                    title="Entfernen"
                    @click="removeCrew(row)"
                  ><i class="fas fa-times"></i></button>
                </div>
                <p v-if="!crewForStage.length" class="crew-empty">
                  Nur {{ primaryBearbeiterName }} — niemand zusätzlich eingeteilt.
                </p>
                <button
                  v-if="isEditing && crewForStage.length < MAX_CREW"
                  type="button"
                  class="crew-add"
                  @click="addCrew"
                ><i class="fas fa-plus"></i> Bearbeiter hinzufügen</button>
                <p class="crew-hint">
                  Eingeteilte Bearbeiter dürfen diese Etappe stempeln
                  (Start / Pause / Weiter / Fertig).
                </p>
              </div>
            </div>
            <div class="info-row">
              <span class="info-label">Bemerkung ({{ getStatusText(editableOrder.status) }}, max. {{ stageNoteMax }})</span>
              <textarea
                v-model="stageNote"
                rows="3"
                class="edit-input edit-textarea"
                :maxlength="stageNoteMax"
                placeholder="Aufklappbare Bemerkung zu dieser Etappe …"
                :disabled="!isEditing"
              ></textarea>
            </div>
          </div>
        </div>

        <!-- Notizen -->
        <div class="info-card">
          <div class="info-card-header">
            <i class="fas fa-sticky-note"></i>
            <h3>Notizen</h3>
          </div>
          <div class="info-card-body">
            <textarea
              v-model="editableOrder.notes"
              rows="4"
              class="edit-input edit-textarea"
              placeholder="Interne Notizen …"
              :disabled="!isEditing"
            ></textarea>

            <div class="info-row" style="margin-top:1rem">
              <span class="info-label">Handnotiz</span>
              <textarea
                v-model="editableOrder.handnotiz"
                rows="2"
                class="edit-input edit-textarea"
                placeholder="Handschriftliche Ergänzung …"
                :disabled="!isEditing"
              ></textarea>
            </div>
            <div v-if="isOwner || editableOrder.prinance_admin_comment" class="info-row">
              <span class="info-label">Admin-Kommentar (Prinance)</span>
              <textarea
                v-model="editableOrder.prinance_admin_comment"
                rows="2"
                class="edit-input edit-textarea"
                placeholder="Kommentar zu den importierten Prinance-Daten …"
                :disabled="!isEditing || !isOwner"
              ></textarea>
            </div>
          </div>
        </div>
      </div>

      <!-- ─── RIGHT COLUMN ─── -->
      <div class="relations-section">

        <!-- Customer -->
        <div class="info-card" v-if="customer">
          <div class="info-card-header">
            <i class="fas fa-building"></i>
            <h3>Kunde</h3>
          </div>
          <div class="info-card-body">
            <div class="customer-header">
              <h4>
                <router-link :to="`/kunden/${customer.id}`" class="customer-name-link">
                  {{ customer.name }}
                </router-link>
              </h4>
              <span v-if="customer.name_suffix" class="customer-suffix">{{ customer.name_suffix }}</span>
            </div>
            <div v-if="customer.address" class="info-row">
              <span class="info-label">Anschrift</span>
              <span class="info-value">{{ customer.address }}</span>
            </div>
            <div v-if="customer.postal_code || customer.city" class="info-row">
              <span class="info-label">Ort</span>
              <span class="info-value">{{ customer.postal_code }} {{ customer.city }}</span>
            </div>
            <div v-if="customer.phone" class="info-row">
              <span class="info-label">Telefon</span>
              <a :href="`tel:${customer.phone}`" class="info-value info-link">
                <i class="fas fa-phone fa-xs"></i>
                {{ customer.phone }}
              </a>
            </div>
            <div v-if="customer.email" class="info-row">
              <span class="info-label">E-Mail</span>
              <a :href="`mailto:${customer.email}`" class="info-value info-link">
                <i class="fas fa-envelope fa-xs"></i>
                {{ customer.email }}
              </a>
            </div>
          </div>
        </div>

        <!-- Bearbeiter -->
        <div class="info-card" v-if="employee">
          <div class="info-card-header">
            <i class="fas fa-user-tie"></i>
            <h3>Bearbeiter</h3>
          </div>
          <div class="info-card-body">
            <div class="customer-header">
              <h4>
                <router-link :to="`/mitarbeiter/${employee.id}`" class="customer-name-link">
                  {{ employee.name }}
                </router-link>
              </h4>
            </div>
            <div v-if="employee.abteilung" class="info-row">
              <span class="info-label">Abteilung</span>
              <span class="info-value">{{ employee.abteilung }}</span>
            </div>
            <div v-if="employee.tel" class="info-row">
              <span class="info-label">Telefon</span>
              <a :href="`tel:${employee.tel}`" class="info-value info-link">
                <i class="fas fa-phone fa-xs"></i>
                {{ employee.tel }}
              </a>
            </div>
            <div v-if="employee.email" class="info-row">
              <span class="info-label">E-Mail</span>
              <a :href="`mailto:${employee.email}`" class="info-value info-link">
                <i class="fas fa-envelope fa-xs"></i>
                {{ employee.email }}
              </a>
            </div>
          </div>
        </div>

        <!-- Contacts -->
        <div class="info-card" v-if="contacts && contacts.length > 0">
          <div class="info-card-header">
            <i class="fas fa-users"></i>
            <h3>Ansprechpartner</h3>
          </div>
          <div class="info-card-body">
            <div v-for="contact in contacts" :key="contact.id" class="contact-item">
              <div class="contact-name">
                <strong>{{ contact.salutation }} {{ contact.first_name }} {{ contact.last_name }}</strong>
              </div>
              <div v-if="contact.department" class="contact-department">{{ contact.department }}</div>
              <div class="contact-details">
                <a v-if="contact.phone" :href="`tel:${contact.phone}`" class="contact-link">
                  <i class="fas fa-phone fa-xs"></i> {{ contact.phone }}
                </a>
                <a v-if="contact.email" :href="`mailto:${contact.email}`" class="contact-link">
                  <i class="fas fa-envelope fa-xs"></i> {{ contact.email }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>

    <!-- Bottom action bar (mobile: all actions; desktop: save/cancel only, since header has them too) -->
    <div v-if="!loading && isEditing" class="bottom-action-bar">
      <button type="button" class="btn-action btn-cancel" @click="cancelEdit" :disabled="saving">
        <i class="fas fa-times"></i><span>Abbrechen</span>
      </button>
      <button type="button" class="btn-action btn-save" @click="saveOrder" :disabled="saving">
        <i v-if="saving" class="fas fa-spinner fa-spin"></i>
        <i v-else class="fas fa-save"></i>
        <span>Speichern</span>
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import FilterDropdown from '@/components/FilterDropdown.vue'
import { getMachineList, isExternMachine, EXTERN_MACHINE } from '@/utils/machineNames'
import { ORDER_CONSTANTS, rasterOptionsFor } from '@/services/orderService'

export default {
  name: 'OrderEdit',
  components: { FilterDropdown },

  beforeRouteLeave(to, from, next) {
    if (this.isEditing && this.hasUnsavedChanges) {
      if (!window.confirm('Es gibt ungespeicherte Änderungen. Trotzdem verlassen?')) {
        return next(false)
      }
    }
    next()
  },

  data() {
    return {
      order: {},
      editableOrder: {},
      originalOrder: {},
      customer: null,
      employee: null,
      contacts: [],
      isEditing: true,
      loading: false,
      saving: false,
      saveErrors: [],
      fieldErrors: {},
      employeeList: [],
      // Req #8 — editable copy of order.machine_assignments. Rows carry a local
      // _key so Vue keeps inputs stable while rows are added/moved/removed;
      // deletions are collected in removedAssignmentIds for the _destroy flags.
      assignments: [],
      originalAssignments: '[]', // JSON snapshot for the unsaved-changes guard
      removedAssignmentIds: [],
      assignmentKeySeq: 0,
      // Boss-Mail — weitere Bearbeiter je Etappe. Wie bei #8 tragen die Zeilen
      // einen lokalen _key, damit Vue die Selects beim Hinzufügen/Entfernen
      // stabil hält; Löschungen sammeln sich in removedCrewIds für _destroy.
      crew: [],
      originalCrew: '[]',
      removedCrewIds: [],
      crewKeySeq: 0,
      MAX_CREW: 3
    }
  },

  computed: {
    ...mapGetters(['user', 'can']),

    // „isOwner" = A-Admin (nur der darf die Prinance-Admin-Felder bearbeiten).
    isOwner() { return this.can('manage_users') },

    // #17 — only the Kap.Pl. admins (owner/manager → edit_any_order) may confirm
    // a plan; a Bearbeiter editing their own stage must not flip the board.
    canPlan() { return this.can('edit_any_order') },

    planningStateOptions() {
      return ORDER_CONSTANTS.PLANNING_STATE_OPTIONS
    },

    // A-5 — the three standard rulings, plus whatever this order already carries
    // (see rasterOptionsFor: Prinance LE 38 is free text).
    rasterOptions() {
      return rasterOptionsFor(this.editableOrder.raster_output)
    },

    // #8 — the first assignment is the Hauptmaschine; the backend mirrors it
    // into orders.maschine_name after save.
    primaryMachine() { return this.assignments[0]?.maschine_name || '' },

    // Komplett-Extern (#11) — drives the red hint under the Hauptmaschine.
    isExtern() { return isExternMachine(this.primaryMachine) },

    canEdit() {
      if (!this.user || !this.order.id) return false
      return this.can('edit_any_order') || this.order.employee_id === this.user.id
    },

    hasUnsavedChanges() {
      if (JSON.stringify(this.editableOrder) !== JSON.stringify(this.originalOrder)) return true
      // #8 — the assignment rows live outside editableOrder, so compare them too
      // or the "leave without saving?" guard misses a machine change entirely.
      if (JSON.stringify(this.assignments) !== this.originalAssignments) return true
      return JSON.stringify(this.crew) !== this.originalCrew
    },

    // Bemerkung je Status (JSON stage_notes[status]). CtP + „ausgeliefert" = 250,
    // sonst 500 Zeichen (Chef-Vorgabe).
    // Die Crew ist etappenbezogen — angezeigt wird immer die Etappe, auf der
    // der Auftrag gerade steht (dieselbe, die das Zeitpanel stempelt).
    crewForStage() {
      return this.crew.filter(c => c.stage === this.editableOrder.status)
    },

    primaryBearbeiterName() {
      const e = this.employeeList.find(u => u.id === this.editableOrder.employee_id)
      return e ? `${e.vorname} ${e.nachname}`.trim() : 'der zuständige Bearbeiter'
    },

    stageNoteMax() {
      return ['plates_complete', 'delivered'].includes(this.editableOrder.status) ? 250 : 500
    },
    stageNote: {
      get() {
        return (this.editableOrder.stage_notes || {})[this.editableOrder.status] || ''
      },
      set(val) {
        const notes = { ...(this.editableOrder.stage_notes || {}) }
        notes[this.editableOrder.status] = val
        this.editableOrder.stage_notes = notes
      }
    },

    deliveryDate: {
      get() {
        return this.buildDatetimeLocal(
          this.editableOrder.delivery_year,
          this.editableOrder.delivery_month,
          this.editableOrder.delivery_day,
          this.editableOrder.delivery_hour
        )
      },
      set(value) {
        if (!value) {
          this.editableOrder.delivery_year = 0
          this.editableOrder.delivery_month = 0
          this.editableOrder.delivery_day = 0
          this.editableOrder.delivery_hour = 0
          return
        }
        const d = new Date(value)
        this.editableOrder.delivery_year = d.getFullYear()
        this.editableOrder.delivery_month = d.getMonth() + 1
        this.editableOrder.delivery_day = d.getDate()
        this.editableOrder.delivery_hour = d.getHours()
      }
    },

    printDate: {
      get() {
        return this.buildDatetimeLocal(
          this.editableOrder.print_year,
          this.editableOrder.print_month,
          this.editableOrder.print_day,
          this.editableOrder.print_hour
        )
      },
      set(value) {
        if (!value) {
          this.editableOrder.print_year = 0
          this.editableOrder.print_month = 0
          this.editableOrder.print_day = 0
          this.editableOrder.print_hour = 0
          return
        }
        const d = new Date(value)
        this.editableOrder.print_year = d.getFullYear()
        this.editableOrder.print_month = d.getMonth() + 1
        this.editableOrder.print_day = d.getDate()
        this.editableOrder.print_hour = d.getHours()
      }
    }
  },

  async mounted() {
    await Promise.all([this.loadOrder(), this.loadEmployees()])
    // If the assigned employee is archived (not in active list), add them so the dropdown still shows them
    if (this.employee && !this.employeeList.find(e => e.id === this.employee.id)) {
      this.employeeList = [...this.employeeList, this.employee]
    }
  },

  methods: {
    ...mapActions(['fetchData']),

    // ── Req #8 — Maschinen-Belegung ────────────────────────────────────────

    // The API hands out ISO timestamps ("2026-07-27T08:00:00.000Z"); the
    // <input type="datetime-local"> wants "YYYY-MM-DDTHH:MM". No timezone maths
    // on purpose — the app stores wall-clock times, same as the Kap.Pl. drag
    // handler does when it writes them back.
    toLocalInput(iso) {
      return iso ? String(iso).replace(' ', 'T').slice(0, 16) : ''
    },

    // "YYYY-MM-DDTHH:MM" → "YYYY-MM-DD HH:MM:SS" (what Rails parses back).
    toRailsDateTime(local) {
      return local ? `${String(local).replace('T', ' ')}:00`.slice(0, 19) : null
    },

    loadAssignments(rows) {
      this.removedAssignmentIds = []
      this.assignments = (rows || []).map(a => ({
        _key: this.assignmentKeySeq++,
        id: a.id,
        maschine_name: a.maschine_name || '',
        geplant_start: this.toLocalInput(a.geplant_start),
        geplant_ende: this.toLocalInput(a.geplant_ende),
        geplante_menge: a.geplante_menge,
        schicht_nr: a.schicht_nr
      }))
      this.originalAssignments = JSON.stringify(this.assignments)
    },

    addAssignment() {
      this.assignments.push({
        _key: this.assignmentKeySeq++,
        id: null,
        maschine_name: '',
        geplant_start: '',
        geplant_ende: '',
        geplante_menge: null,
        schicht_nr: null
      })
    },

    removeAssignment(index) {
      const [removed] = this.assignments.splice(index, 1)
      // Rows that exist server-side need an explicit _destroy on save; rows that
      // were only ever local just disappear.
      if (removed?.id) this.removedAssignmentIds.push(removed.id)
    },

    moveAssignment(index, delta) {
      const target = index + delta
      if (target < 0 || target >= this.assignments.length) return
      const rows = this.assignments
      ;[rows[index], rows[target]] = [rows[target], rows[index]]
    },

    // Nested attributes for the PUT. `position` follows the visible order, so
    // row 1 is the Hauptmaschine the legacy maschine_name column mirrors.
    loadCrew(rows) {
      this.crew = (rows || []).map(r => ({
        _key: this.crewKeySeq++,
        id: r.id,
        user_id: r.user_id,
        stage: r.stage
      }))
      this.originalCrew = JSON.stringify(this.crew)
      this.removedCrewIds = []
    },

    addCrew() {
      this.crew.push({
        _key: this.crewKeySeq++,
        id: null,
        user_id: null,
        stage: this.editableOrder.status
      })
    },

    removeCrew(row) {
      if (row.id) this.removedCrewIds.push(row.id)
      this.crew = this.crew.filter(c => c._key !== row._key)
    },

    crewPayload() {
      const rows = this.crew
        .filter(c => c.user_id)
        .map(c => ({ id: c.id || undefined, user_id: c.user_id, stage: c.stage }))
      const destroyed = this.removedCrewIds.map(id => ({ id, _destroy: true }))
      return [...rows, ...destroyed]
    },

    assignmentsPayload() {
      const rows = this.assignments
        .filter(a => a.maschine_name)
        .map((a, i) => ({
          id: a.id || undefined,
          maschine_name: a.maschine_name,
          geplant_start: this.toRailsDateTime(a.geplant_start),
          geplant_ende: this.toRailsDateTime(a.geplant_ende),
          geplante_menge: a.geplante_menge === '' ? null : a.geplante_menge,
          schicht_nr: a.schicht_nr,
          position: i
        }))

      const destroyed = this.removedAssignmentIds.map(id => ({ id, _destroy: true }))
      return [...rows, ...destroyed]
    },

    async loadOrder() {
      this.loading = true
      try {
        const response = await this.fetchData({ endpoint: `orders/${this.$route.params.id}` })
        this.order = response.order
        this.customer = response.customer
        this.employee = response.employee
        this.contacts = response.contacts || []
        this.editableOrder = { ...response.order }
        this.originalOrder = { ...this.editableOrder }
        this.loadAssignments(response.order.machine_assignments)
        this.loadCrew(response.order.stage_assignments)
      } catch (err) {
        console.error('Failed to load order:', err)
        this.$toast?.error('Auftrag konnte nicht geladen werden')
        this.$router.push('/auftraege')
      } finally {
        this.loading = false
      }
    },

    async loadEmployees() {
      try {
        const response = await this.fetchData({ endpoint: 'users' })
        this.employeeList = response.data || response || []
      } catch (err) {
        console.error('Failed to load employees:', err)
      }
    },

    startEdit() {
      this.originalOrder = { ...this.editableOrder }
      this.saveErrors = []
      this.fieldErrors = {}
      this.isEditing = true
    },

    cancelEdit() {
      this.editableOrder = { ...this.originalOrder }
      this.loadAssignments(this.order.machine_assignments) // #8 — drop row edits too
      this.loadCrew(this.order.stage_assignments)
      this.saveErrors = []
      this.fieldErrors = {}
      this.isEditing = false
    },

    async saveOrder() {
      this.saving = true
      this.saveErrors = []
      this.fieldErrors = {}
      try {
        await this.fetchData({
          endpoint: `orders/${this.order.id}`,
          method: 'PUT',
          params: { order: this.buildPayload() }
        })
        this.order = { ...this.order, ...this.editableOrder }
        this.originalOrder = { ...this.editableOrder }
        this.isEditing = false
        this.$toast?.success('Auftrag gespeichert')
        // Redirect to the record view after a successful save
        this.$router.push(`/auftraege/${this.order.id}`)
      } catch (err) {
        console.error('Save failed:', err)
        const data = err.response?.data
        if (data?.errors) {
          this.fieldErrors = data.errors
          this.saveErrors = Object.entries(data.errors)
            .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
        } else if (data?.error) {
          this.saveErrors = [data.error]
        } else {
          this.saveErrors = ['Speichern fehlgeschlagen']
        }
        this.$toast?.error('Fehler beim Speichern')
      } finally {
        this.saving = false
      }
    },

    buildPayload() {
      const o = this.editableOrder
      return {
        order_number: o.order_number,
        invoice_number: o.invoice_number,
        status: o.status,
        payment_status: o.payment_status,
        priority: o.priority,
        pk_sb: o.pk_sb,
        employee_id: o.employee_id || null,
        description: o.description,
        print_run: o.print_run,
        druckform_count: o.druckform_count,
        page_count: o.page_count,
        color: o.color,
        format: o.format,
        raster_output: o.raster_output,
        planning_state: o.planning_state, // #17
        farbigkeit: o.farbigkeit,
        paper_type: o.paper_type,
        paper_weight: o.paper_weight,
        material_status: o.material_status,
        delivery_method: o.delivery_method,
        papier_bestellt: o.papier_bestellt,
        papier_bestellt_am: o.papier_bestellt_am,
        papier_angeliefert: o.papier_angeliefert,
        druckbereit_anzeige: o.druckbereit_anzeige,
        final_width: o.final_width,
        final_length: o.final_length,
        print_width: o.print_width,
        print_length: o.print_length,
        delivery_year: o.delivery_year,
        delivery_month: o.delivery_month,
        delivery_day: o.delivery_day,
        delivery_hour: o.delivery_hour,
        print_year: o.print_year,
        print_month: o.print_month,
        print_day: o.print_day,
        print_hour: o.print_hour,
        notes: o.notes,
        // #8 — maschine_name is derived: Order#sync_primary_machine! mirrors
        // assignment 0 into it after save. Sending it here would just race.
        machine_assignments_attributes: this.assignmentsPayload(),
        stage_assignments_attributes: this.crewPayload(),
        is_schoen_wider: o.is_schoen_wider,
        is_umschlagen: o.is_umschlagen,
        is_umstuelpen: o.is_umstuelpen,
        is_wendeeinrichtung: o.is_wendeeinrichtung,
        position_code: o.position_code,
        handnotiz: o.handnotiz,
        prinance_admin_comment: o.prinance_admin_comment,
        verbrauchtes_papier: o.verbrauchtes_papier,
        plattenanzahl: o.plattenanzahl,
        partner_order: o.partner_order,
        druckfreigabe_stunden: o.druckfreigabe_stunden,
        druckfreigabe_datum: o.druckfreigabe_datum,
        stage_notes: o.stage_notes || {}
      }
    },

    goBack() {
      if (this.isEditing && this.hasUnsavedChanges) {
        if (!window.confirm('Es gibt ungespeicherte Änderungen. Trotzdem verlassen?')) return
      }
      this.$router.push(`/auftraege/${this.order.id}`)
    },

    buildDatetimeLocal(year, month, day, hour) {
      if (!year || !month || !day) return ''
      const pad = n => String(n).padStart(2, '0')
      return `${year}-${pad(month)}-${pad(day)}T${pad(hour || 0)}:00`
    },

    formatDate(dateString) {
      if (!dateString) return '–'
      return new Date(dateString).toLocaleDateString('de-DE')
    },

    formatDateTime(dt) {
      if (!dt) return '–'
      return new Date(dt).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })
    },

    fieldError(name) {
      const e = this.fieldErrors[name]
      return e ? (Array.isArray(e) ? e.join(', ') : e) : null
    },

    // ── Status helpers ──
    getStatusClass(status) {
      const map = {
        order_received:      'status-ordered',
        prepress_complete:   'status-prepress',
        plates_complete:     'status-plates',
        printing_complete:   'status-printing',
        processing_complete: 'status-processing',
        shipping_complete:   'status-shipping',
        delivered:           'status-delivered',
        invoice_issued:      'status-invoiced',
        archived:            'status-archived'
      }
      return map[status] || ''
    },

    getStatusText(status) {
      const map = {
        order_received: 'Auftrag erteilt',
        prepress_complete: 'Druckfreigabe erteilt',
        plates_complete: 'Druckplatten fertig',
        printing_complete: 'Druck fertig',
        processing_complete: 'WTV fertig',
        shipping_complete: 'Versand fertig',
        delivered: 'Ausgeliefert',
        invoice_issued: 'Rechnung raus',
        archived: 'Archiviert'
      }
      return map[status] || status || '—'
    },

    getPaymentStatusEditClass(status) {
      const map = {
        unpaid: 'pay-unpaid',
        deposit_required: 'pay-dep-req',
        deposit_booked: 'pay-dep-ok',
        cash_on_delivery: 'pay-cod',
        cod_paid: 'pay-cod-ok',
        invoice: 'pay-invoice',
        invoice_paid: 'pay-paid'
      }
      return map[status] || ''
    },

    getPriorityEditClass(priority) {
      const map = {
        urgent: 'prio-urgent',
        critical: 'prio-critical',
        normal: 'prio-normal',
        low: 'prio-low'
      }
      return map[priority] || ''
    },

    getPriorityBadgeClass(priority) {
      const map = {
        urgent: 'priority-high',
        critical: 'priority-high',
        normal: 'priority-medium',
        low: 'priority-low'
      }
      return map[priority] || 'priority-medium'
    },

    // ── Dropdown options ──
    employeeOptions() {
      return this.employeeList.map(e => ({ value: String(e.id), label: e.name, icon_url: e.icon_url, id: e.id }))
    },

    statusOptions() {
      return [
        { value: 'order_received',      label: 'Auftrag erteilt',       color: '#FF0000' },
        { value: 'prepress_complete',   label: 'Druckfreigabe erteilt', color: '#FFCC00' },
        { value: 'plates_complete',     label: 'Druckplatten fertig',   color: '#FFFF99' },
        { value: 'printing_complete',   label: 'Druck fertig',          color: '#00FF00' },
        { value: 'processing_complete', label: 'WTV fertig',            color: '#CCFFFF' },
        { value: 'shipping_complete',   label: 'Versand fertig',        color: '#7030A0' },
        { value: 'delivered',           label: 'Ausgeliefert',          color: '#3366FF' },
        { value: 'invoice_issued',      label: 'Rechnung raus',         color: '#C0C0C0' },
        { value: 'archived',            label: 'Archiviert',            color: '#E0E0E0' }
      ]
    },

    paymentOptions() {
      return [
        { value: 'unpaid',           label: 'Offen',                  color: '#9ca3af' },
        { value: 'deposit_required', label: 'Anzahlung erforderlich', color: '#fb923c' },
        { value: 'deposit_booked',   label: 'Anzahlung gebucht',      color: '#4ade80' },
        { value: 'cash_on_delivery', label: 'Per Nachnahme',          color: '#60a5fa' },
        { value: 'cod_paid',         label: 'Nachnahme bezahlt',      color: '#34d399' },
        { value: 'invoice',          label: 'Per Rechnung',           color: '#f87171' },
        { value: 'invoice_paid',     label: 'Rechnung bezahlt',       color: '#4ade80' }
      ]
    },

    priorityOptions() {
      return [
        { value: 'urgent',   label: 'Dringend (P0)',           color: '#ef4444' },
        { value: 'critical', label: 'Pflicht-Termin (P1)',     color: '#f97316' },
        { value: 'normal',   label: 'Normal (P2)',              color: '#f59e0b' },
        { value: 'low',      label: 'Kein fester Termin (P3)', color: '#10b981' }
      ]
    },

    standortOptions() {
      return [
        { value: 'pk', label: 'PK – Pritzwalk' },
        { value: 'sb', label: 'SB – Strausberg' }
      ]
    },

    deliveryOptions() {
      return [
        { value: '0', label: 'DPD' },
        { value: '1', label: 'Spedition' },
        { value: '2', label: 'Selbstabholer' },
        { value: '3', label: 'Kurier' },
        { value: '4', label: 'Post' }
      ]
    },

    materialStatusOptions() {
      return [
        { value: 'available', label: 'Verfügbar' },
        { value: 'ordered',   label: 'Bestellt' },
        { value: 'pending',   label: 'Ausstehend' }
      ]
    },

    // Machine list from the registry (#9/#40). The current value is included
    // even if it's an unknown legacy name, so the dropdown never hides it.
    // "Extern (X)" is appended unconditionally (#11) — Komplett-Extern has to be
    // selectable even when the registry hasn't loaded or wasn't seeded.
    machineOptions() {
      const opts = getMachineList().map(m => ({ value: m.display_name, label: m.display_name }))
      if (!opts.find(o => isExternMachine(o.value))) {
        opts.push({ value: EXTERN_MACHINE, label: EXTERN_MACHINE })
      }
      // Legacy names already on this order (#8: one per assignment row) stay
      // selectable, otherwise the dropdown would silently drop them on save.
      const inUse = [this.editableOrder.maschine_name, ...this.assignments.map(a => a.maschine_name)]
      for (const name of inUse) {
        if (name && !opts.find(o => o.value === name)) {
          opts.unshift({ value: name, label: `${name} (Alt)` })
        }
      }
      return opts
    },

    getPriorityText(priority) {
      const map = {
        urgent: 'Dringend',
        critical: 'Pflicht-Termin',
        normal: 'Normal',
        low: 'Kein fester Termin'
      }
      return map[priority] || '—'
    }
  }
}
</script>

<style scoped>
/* Maschinen-Belegung (#8) */
.ma-count {
  margin-left: auto;
  background: #e5e7eb;
  color: #374151;
  border-radius: 999px;
  padding: 0.1rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
}
.ma-empty {
  color: #6b7280;
  font-size: 0.85rem;
  margin: 0.5rem 0;
}
.ma-row {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.6rem 0;
  border-bottom: 1px solid #f3f4f6;
}
.ma-row:last-of-type { border-bottom: none; }
.ma-pos {
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  margin-top: 1.15rem;
  border-radius: 50%;
  background: #1d6dff;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ma-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  flex: 1 1 auto;
  min-width: 0;
}
.ma-field {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  flex: 1 1 130px;
  min-width: 0;
}
.ma-field label {
  font-size: 0.7rem;
  color: #6b7280;
  font-weight: 600;
}
.ma-machine { flex: 2 1 180px; }
.ma-narrow  { flex: 0 1 100px; }
.ma-actions {
  display: flex;
  gap: 0.2rem;
  margin-top: 1.05rem;
  flex: 0 0 auto;
}
.ma-btn {
  border: 1px solid #d1d5db;
  background: #fff;
  color: #4b5563;
  border-radius: 6px;
  width: 28px;
  height: 28px;
  cursor: pointer;
  font-size: 0.72rem;
}
.ma-btn:hover:not(:disabled) { background: #f3f4f6; }
.ma-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.ma-btn-danger { color: #dc2626; border-color: #fca5a5; }
.ma-btn-danger:hover:not(:disabled) { background: #fef2f2; }
.ma-add {
  margin-top: 0.6rem;
  border: 1px dashed #9ca3af;
  background: #fff;
  color: #374151;
  border-radius: 6px;
  padding: 0.4rem 0.8rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
}
.ma-add:hover { background: #f9fafb; border-color: #1d6dff; color: #1d6dff; }

/* Komplett-Extern hint (#11) — same red as the OrderFlags ring */
.extern-hint {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.4rem;
  color: #dc2626;
  font-size: 0.78rem;
  font-weight: 600;
}
.extern-hint-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #1d6dff;
  box-shadow: 0 0 0 2px #fff, 0 0 0 4px #dc2626;
  flex: 0 0 auto;
}

/* ════════════════════════════════════════════════════════════
   Base styles copied from ShowView.vue for visual consistency
   ════════════════════════════════════════════════════════════ */
.order-detail-container {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: #f8f9fa;
  min-height: 100vh;
}

/* Professional Header */
.page-header {
  background: white;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1.5rem;
  border-bottom: 1px solid #f3f4f6;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: transparent;
  border: none;
  color: #6b7280;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 6px;
}
.back-button:hover { background: #f3f4f6; color: #374151; }

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.btn-action {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  color: #374151;
}
.btn-action:hover:not(:disabled) { background: #f9fafb; border-color: #9ca3af; }
.btn-action:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-action.btn-edit { background: #3b82f6; border-color: #3b82f6; color: white; }
.btn-action.btn-edit:hover:not(:disabled) { background: #2563eb; border-color: #2563eb; }

.btn-action.btn-save { background: #10b981; border-color: #10b981; color: white; }
.btn-action.btn-save:hover:not(:disabled) { background: #059669; border-color: #059669; }

.btn-action.btn-cancel { color: #6b7280; }

.unsaved-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.8rem;
  background: #fef3c7;
  color: #92400e;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}
.unsaved-pill i { font-size: 0.5rem; }

.readonly-hint {
  font-size: 0.85rem;
  color: #9ca3af;
  font-style: italic;
}

.errors-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.875rem 2rem;
  background: #fee2e2;
  color: #991b1b;
  font-size: 0.875rem;
  border-top: 1px solid #fecaca;
}
.errors-banner i { font-size: 1rem; margin-top: 2px; }

.header-main {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.85rem 1.5rem;
}

.order-info {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.order-icon {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #f59e0b 0%, #ef4444 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.order-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.3rem 0;
  letter-spacing: -0.025em;
}

.invoice-suffix {
  color: #6b7280;
  font-weight: 400;
  font-size: 1rem;
  margin-left: 1rem;
}

.order-meta {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6b7280;
  font-size: 0.9rem;
}
.meta-item i { color: #9ca3af; }

.customer-link { color: #3b82f6; text-decoration: none; transition: color 0.2s; }
.customer-link:hover { color: #2563eb; text-decoration: underline; }

.status-badges {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;
}

.status-badge, .priority-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.025em;
}

/* Status badge colors — exakte Legende aus Auftragsverlauf-BDE.pdf (vgl. globalOrderStyles.scss) */
.status-ordered    { background: #FF0000; color: #000; }
.status-prepress   { background: #FFCC00; color: #000; }
.status-plates     { background: #FFFF99; color: #000; }
.status-printing   { background: #00FF00; color: #000; }
.status-processing { background: #CCFFFF; color: #000; }
.status-shipping   { background: #7030A0; color: #fff; }
.status-delivered  { background: #3366FF; color: #fff; }
.status-invoiced   { background: #C0C0C0; color: #000; }
.status-archived   { background: #E0E0E0; color: #333; }

.priority-high   { background: #fee2e2; color: #991b1b; }
.priority-medium { background: #fef3c7; color: #92400e; }
.priority-low    { background: #d1fae5; color: #065f46; }

/* Content Grid */
.content-grid {
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 1rem;
  padding: 0 1.5rem 1.5rem;
  max-width: 1600px;
  margin: 0 auto;
}

/* Kompakt (Kundenwunsch 2026-07-18): die vielen Formular-Karten der linken
   Spalte in zwei Spalten kacheln, damit möglichst alles auf einen Bildschirm
   passt. Grid (nicht column-count) — multicol würde die absolut positionierten
   Dropdown-Panels wieder abschneiden. Rechte Spalte bleibt ein schmaler Stapel. */
.details-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  align-content: start;
}
.relations-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

/* Info Cards */
.info-card {
  background: white;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.info-card-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.85rem;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.info-card-header i { color: #6b7280; font-size: 0.95rem; }

.info-card-header h3 {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #111827;
}

.info-card-body { padding: 0.5rem 0.85rem; }

.info-row {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 0.6rem;
  padding: 0.3rem 0;
  border-bottom: 1px solid #f3f4f6;
  align-items: center;
}
.info-row:last-child { border-bottom: none; padding-bottom: 0; }
.info-row:first-child { padding-top: 0; }

.info-label {
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
}

.info-value {
  color: #111827;
  font-size: 0.9rem;
  word-break: break-word;
}

.info-link {
  color: #3b82f6;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: color 0.2s;
}
.info-link:hover { color: #2563eb; text-decoration: underline; }

/* ════════════════════════════════════════════════════════════
   Edit-specific input styling — matches ShowView aesthetics
   ════════════════════════════════════════════════════════════ */
.edit-input {
  width: 100%;
  padding: 0.35rem 0.6rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  color: #111827;
  font-size: 0.9rem;
  font-family: inherit;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
  box-sizing: border-box;
}

.edit-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.edit-input:disabled {
  background: #f9fafb;
  color: #4b5563;
  border-color: #e5e7eb;
  cursor: default;
}

.edit-input.has-error {
  border-color: #ef4444;
}
.edit-input.has-error:focus {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.field-error {
  color: #b91c1c;
  font-size: 0.78rem;
  margin-top: 0.25rem;
}

.field-hint {
  color: #9ca3af;
  font-size: 0.75rem;
  margin-top: 0.25rem;
  font-style: italic;
}

.edit-textarea {
  resize: vertical;
  min-height: 56px;
  line-height: 1.4;
}

.dimension-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.dimension-group .edit-input { flex: 1; min-width: 0; }
.dim-x { color: #9ca3af; font-weight: 500; }
.dim-unit { color: #6b7280; font-size: 0.85rem; white-space: nowrap; }

.checkbox-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}
.edit-input-inline { max-width: 180px; }

.check-wrap {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
  user-select: none;
}
.check-wrap input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #3b82f6;
  cursor: pointer;
}
.check-wrap input[type="checkbox"]:disabled { cursor: default; }

/* Colored selects (inherit edit-input padding/border but tinted bg) */
.edit-status-select.status-ordered    { background: #FF0000; color: #000; border-color: #b30000; font-weight: 600; }
.edit-status-select.status-prepress   { background: #FFCC00; color: #000; border-color: #e0b400; font-weight: 600; }
.edit-status-select.status-plates     { background: #FFFF99; color: #000; border-color: #e0e060; font-weight: 600; }
.edit-status-select.status-printing   { background: #00FF00; color: #000; border-color: #00d400; font-weight: 600; }
.edit-status-select.status-processing { background: #CCFFFF; color: #000; border-color: #99e6e6; font-weight: 600; }
.edit-status-select.status-shipping   { background: #7030A0; color: #fff; border-color: #4e2170; font-weight: 600; }
.edit-status-select.status-delivered  { background: #3366FF; color: #fff; border-color: #2348cc; font-weight: 600; }
.edit-status-select.status-invoiced   { background: #C0C0C0; color: #000; border-color: #9e9e9e; font-weight: 600; }
.edit-status-select.status-archived   { background: #E0E0E0; color: #333; border-color: #bdbdbd; font-weight: 600; }

.pay-unpaid    { background: #f3f4f6; color: #374151; }
.pay-dep-req   { background: #fed7aa; color: #7c2d12; }
.pay-dep-ok    { background: #bbf7d0; color: #14532d; }
.pay-cod       { background: #bfdbfe; color: #1e3a8a; }
.pay-cod-ok    { background: #a7f3d0; color: #064e3b; }
.pay-invoice   { background: #fecaca; color: #7f1d1d; }
.pay-paid      { background: #bbf7d0; color: #14532d; }

.prio-urgent   { background: #fecaca; color: #7f1d1d; font-weight: 600; }
.prio-critical { background: #fee2e2; color: #991b1b; font-weight: 600; }
.prio-normal   { background: #fef3c7; color: #92400e; }
.prio-low      { background: #d1fae5; color: #065f46; }

/* Customer card styling (from ShowView) */
.customer-header h4 { margin: 0 0 0.5rem 0; font-size: 1.1rem; }
.customer-name-link { color: #111827; text-decoration: none; transition: color 0.2s; }
.customer-name-link:hover { color: #3b82f6; text-decoration: underline; }
.customer-suffix { color: #6b7280; font-size: 0.9rem; font-weight: 400; }

.contact-item {
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
  margin-bottom: 0.75rem;
}
.contact-item:last-child { margin-bottom: 0; }
.contact-name { font-size: 0.95rem; margin-bottom: 0.25rem; }
.contact-department { font-size: 0.8rem; color: #6b7280; margin-bottom: 0.5rem; }
.contact-details { display: flex; flex-direction: column; gap: 0.25rem; }
.contact-link {
  color: #3b82f6;
  text-decoration: none;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.contact-link:hover { text-decoration: underline; }

/* Loading */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

/* Bottom action bar */
.bottom-action-bar {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  padding: 1rem 1.5rem 1.5rem;
}
.bottom-action-bar .btn-action { min-width: 120px; justify-content: center; }

/* Responsive */
@media (max-width: 1200px) {
  .content-grid { grid-template-columns: 1fr; }
  /* Auf schmalen Screens die Karten wieder gestapelt statt zweispaltig. */
  .details-section { grid-template-columns: 1fr; }
}

@media (max-width: 768px) {
  .header-actions .btn-action,
  .header-actions .readonly-hint { display: none; }
  .header-actions .unsaved-pill { display: inline-flex; }
}

@media (max-width: 640px) {
  .header-top, .header-main { padding: 1rem; }
  .header-main {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  .order-info { flex-direction: column; }
  .status-badges { align-items: flex-start; }
  .content-grid { padding: 0 1rem; }
  .info-card-body { padding: 1rem; }
  .info-row {
    grid-template-columns: 1fr;
    gap: 0.35rem;
    align-items: stretch;
  }
}
/* Boss-Mail — weitere Bearbeiter je Etappe */
.stage-crew { display: flex; flex-direction: column; gap: .4rem; width: 100%; }
.stage-crew-line { display: flex; align-items: center; gap: .4rem; }
.stage-crew-line .edit-input { flex: 1; }
.crew-remove {
  border: 1px solid #d9d3ca; background: #fff; color: #8c2f2f;
  border-radius: 3px; width: 2rem; height: 2rem; cursor: pointer; flex: none;
}
.crew-remove:hover { background: #f8e8e6; }
.crew-add {
  align-self: flex-start; border: 1px dashed #b9b2a7; background: transparent;
  color: #1f4e79; border-radius: 3px; padding: .35rem .7rem; cursor: pointer;
  font-size: .86rem;
}
.crew-add:hover { background: #e7eef6; }
.crew-empty, .crew-hint { margin: 0; font-size: .82rem; color: #6b7280; }
.stage-crew-row .info-label small { display: block; font-weight: 400; color: #8a8f98; }

.chk-inline { display: flex; align-items: center; gap: .45rem; font-size: .9rem; }
.chk-inline input { width: auto; }

</style>
